import ReactECharts from 'echarts-for-react';
import { useIsNarrow } from '@/components/blog/useIsNarrow';
import { useIsDark } from '../../useIsDark';

/**
 * OutreachTimelineChart — blog16, Figure 2: two honest clocks.
 *
 * Chart-type-first: duration ranges on one shared calendar axis → horizontal
 * range bars in days. Each bar is anchored at day 0: the solid segment is the
 * low estimate, the lighter extension runs to the high estimate, so the chart
 * shows a duration range without pretending to know a start date.
 *
 * Rows:
 *   - First investor email: 1–3 weeks of calendar (7–21 days). Engineering is
 *     days; the careful review is the long pole (post prose).
 *   - Complete package, engineering: 8–12 weeks (56–84 days, post prose).
 *   - Complete package, warm-up: 30–45 days running in parallel — mirrors the
 *     "Domain warmup to full volume" row of
 *     public/blog/raisy_outreach_infra_costs.csv (industry warmup consensus,
 *     2026). The dashed line at day 90 marks roughly one quarter.
 *
 * Opaque background; ONE shared day formatter for axis and tooltip (Rule 21).
 * Color is never the only identity — every bar carries a text label.
 */

const BRAND = {
  teal: '#0AB3BF',
  tealSoft: '#99E2E8',
  slate: '#64748B',
  slateSoft: '#B6C2CF',
};

type Segment = {
  row: number;
  low: number;
  high: number;
  color: string;
  colorSoft: string;
  label: string;
  tooltip: string;
};

const ROW_NAMES = ['First investor email', 'Complete package — engineering', 'Complete package — warm-up (parallel)'];
const ROW_SHORT = ['First email', 'Engineering', 'Warm-up (parallel)'];

const SEGMENTS: Segment[] = [
  {
    row: 0,
    low: 7,
    high: 21,
    color: BRAND.teal,
    colorSoft: BRAND.tealSoft,
    label: '1–3 weeks',
    tooltip: 'Days of engineering, then one to three weeks of calendar — the long pole is the careful review, not the code.',
  },
  {
    row: 1,
    low: 56,
    high: 84,
    color: BRAND.teal,
    colorSoft: BRAND.tealSoft,
    label: '8–12 weeks',
    tooltip: 'Engineering for the complete package: follow-up sequences, warmed sending inventory, verification at scale, per-mailbox health monitoring.',
  },
  {
    row: 2,
    low: 30,
    high: 45,
    color: BRAND.slate,
    colorSoft: BRAND.slateSoft,
    label: '30–45 days',
    tooltip: 'Domain warm-up to full volume, ticking in parallel whether anyone codes or not (raisy_outreach_infra_costs.csv).',
  },
];

/** ONE shared formatter — axis ticks and tooltips both speak in days. */
const fmtDays = (v: number) => `${v}`;

export const OutreachTimelineChart = ({
  height,
  dark,
}: {
  height?: number;
  dark?: boolean;
}) => {
  const narrow = useIsNarrow();
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;

  const C = isDark
    ? { bg: '#0F172A', axis: '#E2E8F0', muted: '#94A3B8', grid: '#334155', card: 'bg-slate-900', border: 'border-slate-800', caption: 'text-slate-400' }
    : { bg: '#FFFFFF', axis: '#334155', muted: '#64748B', grid: '#334155', card: 'bg-white', border: 'border-slate-200', caption: 'text-slate-500' };

  // Phone: horizontal bars already fit a 343px column; what breaks is the
  // category gutter and the end-of-bar labels, so shorten both and let the
  // rows breathe with a little more height.
  const gutter = narrow ? 108 : 236;
  const chartHeight = height ?? (narrow ? 280 : 300);

  const option = {
    backgroundColor: C.bg,
    tooltip: {
      trigger: 'item',
      appendToBody: true,
      backgroundColor: '#1E293B',
      borderColor: C.grid,
      textStyle: { color: '#F8FAFC', fontSize: 13 },
      formatter: (p: { dataIndex: number }) => {
        const s = SEGMENTS[p.dataIndex];
        return `<b>${ROW_NAMES[s.row]}</b><br/>${s.label} (${fmtDays(s.low)}–${fmtDays(s.high)} days)<br/><span style="color:${C.muted}">${s.tooltip}</span>`;
      },
    },
    grid: { left: gutter, right: narrow ? 76 : 96, top: 28, bottom: 44 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      interval: 10,
      name: 'days from kickoff',
      nameLocation: 'middle',
      nameGap: 30,
      nameTextStyle: { color: C.muted, fontSize: 11 },
      axisLabel: { color: C.muted, fontSize: narrow ? 10 : 11, formatter: (v: number) => fmtDays(v) },
      splitLine: { lineStyle: { color: C.grid, opacity: 0.25 } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: narrow ? ROW_SHORT : ROW_NAMES,
      axisLabel: { color: C.axis, fontSize: narrow ? 10 : 11.5 },
      axisLine: { lineStyle: { color: C.grid } },
      axisTick: { show: false },
    },
    series: [
      {
        type: 'custom',
        markLine: {
          silent: true,
          symbol: 'none',
          lineStyle: { color: C.muted, type: 'dashed', width: 1.5 },
          label: {
            position: 'insideEndTop',
            color: C.muted,
            fontSize: 11,
            formatter: '≈ one quarter',
          },
          data: [{ xAxis: 90 }],
        },
        renderItem: (
          _params: unknown,
          api: {
            value: (i: number) => number;
            coord: (v: [number, number]) => [number, number];
          }
        ) => {
          const idx = api.value(3);
          const s = SEGMENTS[idx];
          const y = api.coord([0, s.row])[1];
          const xStart = api.coord([0, s.row])[0];
          const xLow = api.coord([s.low, s.row])[0];
          const xHigh = api.coord([s.high, s.row])[0];
          const barH = narrow ? 12 : 16;
          return {
            type: 'group',
            children: [
              {
                type: 'rect',
                shape: { x: xStart, y: y - barH / 2, width: xLow - xStart, height: barH, r: 3 },
                style: { fill: s.color },
              },
              {
                type: 'rect',
                shape: { x: xLow, y: y - barH / 2, width: xHigh - xLow, height: barH, r: 3 },
                style: { fill: s.colorSoft },
              },
              {
                type: 'text',
                style: {
                  x: xHigh + 6,
                  y,
                  text: s.label,
                  fill: C.axis,
                  fontSize: narrow ? 9.5 : 11,
                  fontWeight: 600,
                  align: 'left',
                  verticalAlign: 'middle',
                },
              },
            ],
          };
        },
        data: SEGMENTS.map((s, i) => [s.low, s.high, s.row, i]),
      },
    ],
  };

  return (
    <div className={`my-8 rounded-xl overflow-hidden border ${C.border} ${C.card} p-2`}>
      <ReactECharts option={option} style={{ height: chartHeight }} opts={{ renderer: 'canvas' }} />
      <p className={`mt-2 text-xs ${C.caption} text-center px-4`}>
        Figure 2. Two clocks, both honest. The first investor email is days of engineering inside a one-to-three-week
        calendar window. The complete package is eight-to-twelve weeks of engineering while the 30–45 day warm-up
        clock (raisy_outreach_infra_costs.csv) runs in parallel — about a quarter, done properly.
      </p>
    </div>
  );
};
